import { action, computed, isArrayLike, observable, toJS } from 'mobx';
import get from 'lodash/get';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import { Explorer } from '../models/configuration/explorer/Explorer';
import { FundGroupHeader } from '../models/configuration/FundGroupHeader';
import { ExplorerWatchlist } from '../models/configuration/explorer/ExplorerWatchlist';
import { generateGuid } from '../utils/generateGuid';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { snakeCase } from 'lodash';
import { CultureSearchTransform } from '../models/configuration/CultureSearchTransform';
import { GlobalConfig } from '../models/configuration/GlobalConfig';
import { ConditionalDataConfig } from '../models/configuration/ConditionalDataConfig';
import { IncludeData } from '../models/configuration/IncludeData';
import { RatingImageOptions } from '../models/configuration/components/RatingImageOptions';
import { isNullOrUndefined } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { GlobalEventFlags } from '../models/configuration/global/EventFlags';
import { SelectedModuleMode } from '../models/configuration/SelectedModuleMode';
import { OnFundTypeChangeFlags } from '../models/configuration/OnFundTypeChangeFlags';
import { getFundTypeId } from '../utils/getFundTypeId';
import { GlobalDocumentPreview } from '../models/configuration/global/DocumentPreview';
import { GlobalWatchlistConfig } from '../models/configuration/global/GlobalWatchlistConfig';
import { GlobalStorageConfigSource } from '../models/configuration/global/GlobalStorageConfigSource';
import { GlobalUserFiltersConfig } from '../models/configuration/global/GlobalUserFiltersConfig';
import { FootnoteOptions } from '../models/configuration/disclaimers/FootnoteOptions';
import { FootnoteScope } from '../models/configuration/disclaimers/FootnoteScope';
import { RowConfig } from '../models/configuration/explorer/RowConfig';
import { ExtendedInfo } from '../models/configuration/ExtendedInfo';
import { ExtendedInfoOption } from '../models/configuration/ExtendedInfoOption';
import { DefaultDocumentSelectionState } from '../models/configuration/documents/DefaultDocumentSelectionState';
import { IQueryContextConfigurationDocumentEntityKey } from '@kurtosys/ksys-app-template/dist/models/query';
import { DataTextMapping } from '../models/configuration/DataTextMapping';
import {
    ActionOptions,
    ActionOptionType,
    DownloadControlActions,
} from '../models/configuration/explorer/DownloadControls';
import {
    DocumentsGrid,
    DocumentsGridConditional,
} from '../models/configuration/documents/DocumentsGrid';
import { Filter } from '../models/configuration/filter/Filter';
import { StickyConfig } from '../models/configuration/StickyConfig';
import { ResponsiveConfig } from '../models/configuration/ResponsiveConfig';
import { ComponentConfigurations } from '../models/configuration/ComponentConfigurations';
import { ApplicationConfig } from '../models/configuration/ApplicationConfig';
import { ListenForOptions } from '../models/configuration/ListenForOptions';

export class ConfigStore extends BaseStore {
    @observable configFile = {} as any;
    @observable currentModuleId = undefined; // Module id set by params or tab selection
    @observable currentFundType = undefined; // Fund type set by params or tab selection
    @observable appDataProps = {};
    fundlist;

    constructor(
        stores: Stores,
        configFile,
        currentModuleId,
        currentFundType,
        fundlist,
        appDataProps
    ) {
        super(stores);
        this.configFile = this.orchestrateModuleIds(configFile);
        this.currentFundType = currentFundType;
        // define current module OR determine initial selection from module config selected flag.
        if (currentModuleId) {
            this.currentModuleId =
                currentModuleId.indexOf(this.selectedFundTypeId) === -1
                    ? `${this.selectedFundTypeId}_${currentModuleId}`
                    : currentModuleId;
        } else {
            const initialModule = this.modules.find(m => m.selected === true);
            if (initialModule) {
                this.currentModuleId = initialModule.id;
            }
        }
        this.fundlist = fundlist;
        this.appDataProps = appDataProps;
    }

    orchestrateModuleIds(componentConfig: ComponentConfigurations) {
        componentConfig.fundTypes.forEach(fundType => {
            fundType.modules.forEach(module => {
                module.id = `${getFundTypeId(fundType)}_${module.id}`;
            });
        });
        return componentConfig;
    }

    // APP Config
    // ===================

    @computed
    get appConfig(): ApplicationConfig {
        return (this.configFile && this.configFile.app) || {};
    }

    @computed
    get applicationId(): string | undefined {
        return this.appConfig.applicationId;
    }

    @computed
    get listenFor(): Array<ListenForOptions> {
        return this.appConfig.listenFor || [];
    }

    // GLOBAL Config
    // ===================

    @computed
    get globalConfig(): GlobalConfig {
        return (this.configFile && this.configFile.globalConfig) || {};
    }

    @computed
    get autoExpandFirstRow(): boolean {
        const selectedModuleAutoExpandFirstRow = this.selectedModule.autoExpandFirstRow;
        if (selectedModuleAutoExpandFirstRow !== undefined) {
            return (selectedModuleAutoExpandFirstRow)
        }
        return (this.globalConfig && this.globalConfig.autoExpandFirstRow) || false;
    }

    @computed
    get isAutoExpandFirstRowEnabled(): boolean {
        // This ensures that the preserved expanded state across tabs gets disabled.
        return this.modules.some((module) => module.autoExpandFirstRow);
    }

    @computed
    get globalEventFlags(): GlobalEventFlags {
        return (this.globalConfig && this.globalConfig.eventFlags) || {};
    }

    // Get applicationName
    @computed
    get applicationName() {
        return this.configFile.applicationName;
    }

    @computed
    get documentPreviewEnabled(): boolean {
        const { documentPreview } = this.globalConfig;
        return (documentPreview && documentPreview.enabled) || false;
    }

    @computed
    get documentPreviewIcon(): string {
        const { documentPreview } = this.globalConfig;
        return (
            (this.documentPreviewEnabled && documentPreview.icon) ||
            'PreviewIcon'
        );
    }

    @computed
    get documentPreviewMode(): GlobalDocumentPreview['mode'] {
        const { documentPreview } = this.globalConfig;
        return (this.documentPreviewEnabled && documentPreview.mode) || 'TAB';
    }

    @computed
    get globalWatchlistConfig(): GlobalWatchlistConfig {
        const defaultConfig: GlobalWatchlistConfig = {
            source: GlobalStorageConfigSource.localStorage,
        };
        return {
            ...defaultConfig,
            ...(this.globalConfig.watchlist || {}),
        };
    }

    @computed
    get globalDisclaimersByDocumentType() {
        return this.globalConfig.disclaimersByDocumentType || {};
    }

    @computed
    get globalUserFiltersConfig(): GlobalUserFiltersConfig {
        return this.globalConfig.userFilters;
    }

    @computed
    get globalDocumentEntityKeys(): IQueryContextConfigurationDocumentEntityKey {
        return this.globalConfig.documentEntityKeys || {};
    }

    // TOP-LEVEL FUND TYPE
    // ===================

    // Sets the current module
    @action
    setFundType = fundType => {
        this.currentFundType = getFundTypeId(fundType);
    };

    // Get showFundTypes
    @computed
    get showFundTypes() {
        return this.configFile.showFundTypes || false;
    }

    // Get fund types
    @computed
    get fundTypes() {
        const { fundTypes = [] } = this.configFile;

        return fundTypes;
    }

    // Get fundTypeProperty
    @computed
    get fundTypeProperty() {
        if (
            this.selectedFundType &&
            this.selectedFundType.entity &&
            this.selectedFundType.entity.identifierPropertyForCategory
        ) {
            return this.selectedFundType.entity.identifierPropertyForCategory;
        }

        if (
            this.globalConfig &&
            this.globalConfig.entity &&
            this.globalConfig.entity.identifierPropertyForCategory
        ) {
            return this.globalConfig.entity.identifierPropertyForCategory;
        }

        return this.configFile.fundTypeProperty;
    }

    // Returns selectedFundType for either the currentFundType, the first fundType set as selected, or the first fundType
    @computed
    get selectedFundType() {
        const selectedFundType = this.fundTypes.find(f => {
            const fundTypeId = getFundTypeId(f);
            return this.currentFundType
                ? fundTypeId === this.currentFundType
                : f.selected;
        });
        const fundType = selectedFundType || this.fundTypes[0] || {};
        return fundType;
    }

    @computed
    get selectedFundTypeId(): string {
        return getFundTypeId(this.selectedFundType);
    }

    @computed
    get entityType() {
        if (this.selectedFundType.entity && this.selectedFundType.entity.type) {
            return this.selectedFundType.entity.type;
        }
        if (
            this.globalConfig &&
            this.globalConfig.entity &&
            this.globalConfig.entity.type
        ) {
            return this.globalConfig.entity.type;
        }
        return 'CLSS';
    }

    @computed
    get identifierProperty(): string {
        if (
            this.selectedFundType.entity &&
            this.selectedFundType.entity.identifierProperty
        ) {
            return this.selectedFundType.entity.identifierProperty;
        }
        if (this.globalConfig) {
            if (
                this.globalConfig.entity &&
                this.globalConfig.entity.identifierProperty
            ) {
                return this.globalConfig.entity.identifierProperty;
            }

            // DEPRECATED - moved to entity.identifierProperty
            if (this.globalConfig.shareClassKey) {
                return this.globalConfig.shareClassKey;
            }
        }
        return 'isin';
    }

    @computed
    get disabledEntityFlag(): ConditionalDataConfig | undefined {
        if (
            this.selectedFundType.entity &&
            this.selectedFundType.entity.isDisabled
        ) {
            return this.selectedFundType.entity.isDisabled;
        }
        if (this.globalConfig) {
            if (
                this.globalConfig.entity &&
                this.globalConfig.entity.isDisabled
            ) {
                return this.globalConfig.entity.isDisabled;
            }
            // TODO: deprecate and only rely on entity.isDisabled
            if (this.globalConfig.disabledEntityFlag) {
                return this.globalConfig.disabledEntityFlag;
            }
        }
        return;
    }

    // MODULES FOR SELECTED FUND TYPE
    // ==============================

    // Sets the current module
    @action
    setModule = module => {
        this.currentModuleId = module.id;
    };

    // this method updates the currently selected module based on the initially selected module, if the settings condone it.
    @action
    setDefaultModule = () => {
        if (this.selectedModuleMode === 'SwitchToDefault') {
            const firstModule = this.modules[0];
            const defaultModule = this.modules.find(
                module => module.selected === true
            );
            this.currentModuleId =
                (defaultModule && defaultModule.id) ||
                (firstModule && firstModule.id);
        }
    };

    // Get showFundTypes
    @computed
    get showModules() {
        return this.selectedFundType.showModules || false;
    }

    // Get showFundTypes
    @computed
    get moduleMobileSelector() {
        return this.selectedFundType.moduleMobileSelector || 'tabs';
    }

    // Returns available modules from selectedFundType
    @computed
    get modules() {
        const { modules = [] } = this.selectedFundType;
        return modules;
    }

    @computed
    get globalFundTypeChangeFlags(): OnFundTypeChangeFlags {
        return (
            (this.globalEventFlags && this.globalEventFlags.onFundTypeChange) ||
            {}
        );
    }

    @computed
    get fundTypeChangeFlags(): OnFundTypeChangeFlags {
        return (
            (this.selectedFundType && this.selectedFundType.onChangeFlags) || {}
        );
    }

    @computed
    get clearFiltersOnChange(): boolean {
        // change flags overrides old individual flags
        const { clearFilters } = this.fundTypeChangeFlags;
        if (!isNullOrUndefined(clearFilters)) {
            return clearFilters;
        }
        const { clearFiltersOnChange } = this.selectedFundType;
        if (!isNullOrUndefined(clearFiltersOnChange)) {
            return clearFiltersOnChange;
        }
        // change flags overrides old individual flags for global
        const {
            clearFilters: globalClearFilters,
        } = this.globalFundTypeChangeFlags;
        if (!isNullOrUndefined(globalClearFilters)) {
            return globalClearFilters;
        }
        const { clearFiltersOnFundTypeChange } = this.globalConfig;
        if (!isNullOrUndefined(clearFiltersOnFundTypeChange)) {
            return clearFiltersOnFundTypeChange;
        }
        return true;
    }

    @computed
    get selectedModuleMode(): SelectedModuleMode {
        const { selectedModuleMode } = this.fundTypeChangeFlags;
        if (!isNullOrUndefined(selectedModuleMode)) {
            return selectedModuleMode;
        }

        const {
            selectedModuleMode: globalSelectedModuleMode,
        } = this.globalFundTypeChangeFlags;
        if (!isNullOrUndefined(globalSelectedModuleMode)) {
            return globalSelectedModuleMode;
        }

        return 'MaintainSelection';
    }

    // Returns explorer related configurations
    @computed
    get explorer(): Explorer {
        return this.selectedModuleConfig.explorer || {};
    }

    // This method should return the currently selected module or the first one if not defined. (initial selection should be handled in the constructor)
    @computed
    get selectedModule() {
        const firstModule = this.modules[0];
        const selectedModule = this.modules.find(
            m => m.id === this.currentModuleId
        );
        return selectedModule || firstModule || {};
    }

    @computed
    get isDocumentModule() {
        const { type } = this.selectedModule;
        return type === 'DocumentList';
    }

    // TODO: Rename config => selectedModule Config
    // CONFIG FOR SELECTED MODULE
    // ==========================

    // Returns config for the selected module
    @computed
    get selectedModuleConfig() {
        const { config = {} } = this.selectedModule;

        return config;
    }

    // VALUES DERIVED FROM CONFIG FOR SELECTED MODULE
    // ==============================================

    @computed
    get moduleFootnoteOptions() {
        return this.generateFootnoteOptions(this.selectedModuleConfig);
    }

    @computed
    get noDataText(): string {
        return (
            this.selectedModuleConfig.noDataText ||
            this.selectedFundType.noDataText ||
            'There are no funds matching this search'
        );
    }

    @computed
    get stickyOffset() {
        const { stickyOffset } = this.fundGroupHeader;
        return stickyOffset || this.selectedModuleConfig.stickyOffset || 0;
    }

    @computed
    get defaultCulture() {
        return (
            this.selectedModuleConfig.defaultCulture ||
            this.globalConfig.defaultCulture ||
            'en-GB'
        );
    }

    @computed
    get sourceCulture() {
        return (
            (this.globalConfig && this.globalConfig.sourceCulture) || 'Default'
        );
    }

    @computed
    get useHtmlTranslations() {
        return (
            (this.globalConfig && this.globalConfig.useHtmlTranslations) ||
            false
        );
    }

    @computed
    get paging() {
        if (
            this.selectedModuleConfig.explorer &&
            this.selectedModuleConfig.explorer.paging
        ) {
            return this.selectedModuleConfig.explorer.paging;
        }
        if (this.globalConfig && this.globalConfig.paging) {
            return this.globalConfig.paging;
        }
        return {};
    }

    @computed
    get explorerMode() {
        return this.paging.mode || 'loadMoreButton';
    }

    @computed
    get limit() {
        if (this.selectedModuleConfig.searchLimit) {
            return this.selectedModuleConfig.searchLimit;
        }
        if (this.globalConfig && this.globalConfig.searchLimit) {
            return this.globalConfig.searchLimit;
        }
        return 30;
    }

    @computed
    get sort() {
        const configSort = toJS(this.selectedModuleConfig.sort) || [];
        // replace "app:" place holders with app data props
        const parsedSort = configSort.map(item => {
            item.key = snakeCase(
                replacePlaceholders({
                    text: item.key,
                    appParams: this.appDataProps,
                })
            );
            return item;
        });
        return observable(parsedSort);
    }

    @computed
    get advancedSort() {
        return this.selectedModuleConfig.advancedSort;
    }

    @computed
    get disclaimersByDocumentType() {
        return this.selectedModuleConfig.disclaimersByDocumentType || {};
    }

    @computed
    get watchListProperty() {
        const property =
            (this.watchListConfig && this.watchListConfig.property) ||
            this.selectedModuleConfig.watchListProperty;
        return property;
    }

    @computed
    get watchListEnabled() {
        const enabled =
            (this.watchListConfig && this.watchListConfig.enabled) ||
            this.selectedModuleConfig.watchListEnabled;
        return enabled;
    }

    @computed
    get watchListConfig(): ExplorerWatchlist {
        return (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.explorer &&
            this.selectedModuleConfig.explorer.watchlist
        );
    }

    @computed
    get responsiveConfig(): ResponsiveConfig | undefined {
        return (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.responsiveConfig
        );
    }
    @computed
    get fundGroupHeader(): FundGroupHeader {
        const fundGroupHeader = this.selectedModuleConfig.fundGroupHeader || {};
        const fundGroupProperty = this.selectedModuleConfig.fundGroupProperty;
        const stickyOffset = this.selectedModuleConfig.stickyOffset || 0;
        return {
            fundGroupProperty,
            stickyOffset,
            ...fundGroupHeader,
        };
    }

    @computed
    get hasResponsiveLayoutHeader(): boolean {
        return !!this.stores.screenSize.headerLayout;
    }

    @computed
    get fundGroupProperty() {
        const { fundGroupProperty } = this.fundGroupHeader;
        return fundGroupProperty || this.selectedModuleConfig.fundGroupProperty;
    }

    @computed
    get fundSubGroupProperty() {
        const { fundSubGroupProperty } = this.fundGroupHeader;
        return (
            fundSubGroupProperty ||
            this.selectedModuleConfig.fundSubGroupProperty
        );
    }

    @computed
    get nonGroupHeaderText() {
        const { nonGroupHeaderText } = this.fundGroupHeader;
        return (
            nonGroupHeaderText || this.selectedModuleConfig.nonGroupHeaderText
        );
    }

    /**
     * Update action link elements to contain a unique key, in order for segmentation validation to work.
     *
     * @param actions
     */
    generateKeyForLinkElement(actions: DownloadControlActions) {
        if (actions.elements?.length > 0) {
            for (const element of actions.elements) {
                if (element.type === 'link' && !element._key) {
                    element._key = generateGuid();
                }
            }
        }
    }

    @computed
    get globalDownloadControlActions(): DownloadControlActions {
        const configActions = this.globalConfig?.downloadControlActions;
        const defaultElements: Array<ActionOptions> = [
            {
                type: ActionOptionType.download,
            },
        ];
        if (this.documentPreviewEnabled) {
            defaultElements.push({
                type: ActionOptionType.preview,
                labelOptions: {
                    hideWhen: 'inline',
                },
            });
        }
        if (!configActions) {
            return {
                disableExtendedInfo: false,
                elements: defaultElements,
            };
        }
        if (!configActions.elements) {
            return { ...configActions, elements: defaultElements };
        }
        this.generateKeyForLinkElement(configActions);
        return configActions;
    }

    @computed
    get groupHeaderDownloadControlActions(): DownloadControlActions {
        const globalConfig = this.globalDownloadControlActions;
        const groupHeaderConfig = this.fundGroupHeader?.actions;
        if (!groupHeaderConfig) {
            return {
                ...globalConfig,
                disableExtendedInfo: true,
            };
        }
        if (!groupHeaderConfig.elements) {
            return {
                ...groupHeaderConfig,
                elements: globalConfig.elements,
            };
        }
        this.generateKeyForLinkElement(groupHeaderConfig);
        return groupHeaderConfig;
    }

    getDownloadControlActionsOrDefault(
        config?: DownloadControlActions
    ): DownloadControlActions {
        const globalConfig = this.globalDownloadControlActions;
        if (!config) {
            return globalConfig;
        }
        if (!config.elements) {
            return { ...config, elements: globalConfig.elements };
        }
        return config;
    }

    @computed
    get fundCurrencyProperty() {
        return this.selectedModuleConfig.fundCurrencyProperty;
    }

    @computed
    get identifierSearch() {
        return this.selectedModuleConfig.identifierSearch || {};
    }

    @computed
    get documentsJoinSpec() {
        return this.selectedModuleConfig.documentsJoinSpec || [];
    }

    @computed
    get excludeExpiredDocuments() {
        return this.selectedModuleConfig.excludeExpiredDocuments || false;
    }

    @computed
    get timeseriesInclude() {
        const { timeseriesInclude } = this.selectedModuleConfig;
        if (this.isExcluded(timeseriesInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return timeseriesInclude || {};
    }

    @computed
    get allocationsInclude() {
        const { allocationsInclude } = this.selectedModuleConfig;
        if (this.isExcluded(allocationsInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return allocationsInclude || {};
    }

    @computed
    get statisticsInclude() {
        const { statisticsInclude } = this.selectedModuleConfig;
        if (this.isExcluded(statisticsInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return statisticsInclude || {};
    }

    @computed
    get commentariesInclude() {
        const { commentariesInclude } = this.selectedModuleConfig;
        if (this.isExcluded(commentariesInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return commentariesInclude || {};
    }

    @computed
    get disclaimersInclude() {
        const { disclaimersInclude } = this.selectedModuleConfig;
        if (this.isExcluded(disclaimersInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return disclaimersInclude || {};
    }

    @computed
    get fundManagersInclude() {
        const { fundManagersInclude } = this.selectedModuleConfig;
        if (this.isExcluded(fundManagersInclude)) {
            // Due to backwards compatibility, check explicit exclude property to exclude from API call
            return;
        }
        return fundManagersInclude || {};
    }

    @computed
    get includeProperties() {
        const { includeProperties } = this.selectedModuleConfig;
        return includeProperties;
    }

    @computed
    get historicalDatasetsInclude() {
        const { historicalDatasetsInclude } = this.selectedModuleConfig;
        return historicalDatasetsInclude;
    }

    isExcluded(includeData: IncludeData) {
        return includeData && includeData.exclude;
    }

    @computed
    get filterOptions() {
        if (this.selectedModuleConfig.filterConfig) {
            return this.selectedModuleConfig.filterConfig;
        }

        const filtersTopExpanded = this.selectedModuleConfig.hasOwnProperty(
            'filtersTopExpanded'
        )
            ? this.selectedModuleConfig.filtersTopExpanded
            : true;

        const filtersTopExpandable = this.selectedModuleConfig.hasOwnProperty(
            'filtersTopExpandable'
        )
            ? this.selectedModuleConfig.filtersTopExpandable
            : true;

        return {
            filtersTopExpanded,
            filtersTopExpandable,
            top: this.selectedModuleConfig.filtersTop,
            clearIndividualFilters: this.selectedModuleConfig
                .clearIndividualFilters,
            shareableLink:
                this.selectedModuleConfig &&
                this.selectedModuleConfig.filterConfig &&
                this.selectedModuleConfig.filterConfig.shareableLink,
            filters: this.selectedModuleConfig.filters,
            minimumFiltersNeeded:
                this.selectedModuleConfig.hasOwnProperty(
                    'minimumFiltersNeeded'
                ) && this.selectedModuleConfig.minimumFiltersNeeded,
        };
    }

    @computed
    get filtersConfig(): Array<Filter> {
        return this.filterOptions.filters || [];
    }

    @computed
    get filtersConfigByProperty() {
        return (
            (this.filtersConfig &&
                this.filtersConfig.reduce((config, filterConfig) => {
                    if (!config[filterConfig.property]) {
                        config[filterConfig.property] = filterConfig;
                    }
                    return config;
                }, {})) ||
            {}
        );
    }

    isDocumentFilter(property: string): boolean {
        const filterConfig =
            this.filtersConfigByProperty &&
            this.filtersConfigByProperty[property];
        return (
            filterConfig &&
            ['DOCUMENT_META', 'DOCUMENT_INFERRED', 'DOCUMENT_PROPERTY'].some(
                value => value === filterConfig.type
            )
        );
    }

    isDocumentPropertyFilter(property: string): boolean {
        const filterConfig =
            this.filtersConfigByProperty &&
            this.filtersConfigByProperty[property];
        return filterConfig && filterConfig.type === 'DOCUMENT_PROPERTY';
    }

    isDocumentMetaFilter(property: string): boolean {
        const filterConfig =
            this.filtersConfigByProperty &&
            this.filtersConfigByProperty[property];
        return filterConfig && filterConfig.type === 'DOCUMENT_META';
    }

    isDocumentInferredFilter(property: string): boolean {
        const filterConfig =
            this.filtersConfigByProperty &&
            this.filtersConfigByProperty[property];
        return filterConfig && filterConfig.type === 'DOCUMENT_INFERRED';
    }

    @computed
    get clearIndividualFilters() {
        return this.filterOptions.clearIndividualFilters;
    }

    @computed
    get filtersTop() {
        return this.filterOptions.top;
    }

    @computed
    get clearFiltersLabel() {
        return this.filterOptions.clearFiltersLabel;
    }

    @computed
    get shareableLink() {
        return this.filterOptions.shareableLink;
    }

    @computed
    get filtersTopExpanded() {
        return this.filterOptions.hasOwnProperty('topExpanded')
            ? this.filterOptions.topExpanded
            : true;
    }

    @computed
    get filtersTopExpandable() {
        return this.filterOptions.hasOwnProperty('topExpandable')
            ? this.filterOptions.topExpandable
            : true;
    }

    @computed
    get documentSearch() {
        return this.selectedModuleConfig.documentSearch || [];
    }

    @computed
    get globalDocumentSearch() {
        return this.selectedModuleConfig.globalDocumentSearch || [];
    }

    @computed
    get documentSearchBySegmentation() {
        return get(
            this.selectedModuleConfig.documentSearchBySegmentation,
            this.fundlist,
            []
        );
    }

    @computed
    get documentSegmentationFilters() {
        return get(
            this.selectedModuleConfig.documentSegmentationFilters,
            this.fundlist,
            {}
        );
    }

    @computed
    get documentGroupMeta() {
        return this.selectedModuleConfig.documentGroupMeta || '';
    }

    @computed
    get documentGroupInferred(): string | undefined {
        return this.selectedModuleConfig.documentGroupInferred;
    }

    @computed
    get documentGroupOrder() {
        return this.selectedModuleConfig.documentGroupOrder || [];
    }

    @computed
    get documentGroupOrderMetaPropertySort() {
        return (
            this.selectedModuleConfig.documentGroupOrderMetaPropertySort || {}
        );
    }

    @computed
    get documentGroupOrderInferredPropertySort() {
        return (
            this.selectedModuleConfig.documentGroupOrderInferredPropertySort ||
            {}
        );
    }

    @computed
    get documentUngroupedLabel() {
        return this.selectedModuleConfig.documentUngroupedLabel || '';
    }

    @computed
    get moduleShowSingleDocument() {
        const { showSingleDocument = true } = this.selectedModuleConfig;
        return showSingleDocument;
    }

    @computed
    get noDataPlaceholder() {
        return this.selectedModuleConfig.noDataPlaceholder || '–';
    }

    @computed
    get cultureAliases() {
        const globalCultureAliases =
            this.globalConfig &&
            this.globalConfig.valueAliases &&
            this.globalConfig.valueAliases.cultures;
        return (
            {
                ...globalCultureAliases,
                ...this.selectedModuleConfig.cultureAliases,
            } || {}
        );
    }

    @computed
    get selectedDocumentsLabel() {
        return (
            this.selectedModuleConfig.selectedDocumentsLabel || {
                singular: `{selected}/{total} document selected`,
                plural: `{selected}/{total} documents selected`,
            }
        );
    }

    @computed
    get availableDocumentsLabel() {
        return (
            this.selectedModuleConfig.availableDocumentsLabel || {
                singular: `{total} document available`,
                plural: `{total} documents available`,
            }
        );
    }

    @computed
    get columnConfig() {
        const columnConfig = this.selectedModuleConfig.columnConfig || [];
        // Create unique key for link filtering
        for (const column of columnConfig) {
            const uniqueKey = generateGuid();
            column.key = column.key || uniqueKey;
            if (column.actions?.elements?.length > 0) {
                for (const element of column.actions.elements) {
                    if (
                        element.type === ActionOptionType.link &&
                        !element._key
                    ) {
                        element._key = generateGuid();
                    }
                }
            }
            // Generate an unique id for the documents grid if not configured
            const { columnDocumentsGrid } = column;
            if (columnDocumentsGrid && !columnDocumentsGrid.id) {
                column.columnDocumentsGrid.id = uniqueKey;
            }
        }
        return columnConfig;
    }

    @computed
    get headerDisclaimerTypes() {
        return this.selectedModuleConfig.headerDisclaimerTypes || [];
    }

    // If present, config.footerDisclaimerTypes takes precedence over config.disclaimerTypes
    // config.disclaimerTypes should be considered a legacy option
    @computed
    get footerDisclaimerTypes() {
        return (
            this.selectedModuleConfig.footerDisclaimerTypes ||
            this.selectedModuleConfig.disclaimerTypes ||
            []
        );
    }

    @computed
    get performanceCheck() {
        return this.selectedModuleConfig.performanceCheck || {};
    }

    @computed
    get segmentationFilter() {
        return this.selectedModuleConfig.segmentationFilter || {};
    }

    @computed
    get documentFilename(): DataTextMapping {
        return this.selectedModuleConfig.documentFilename || '';
    }

    @computed
    get documentDisplayName(): DataTextMapping {
        return this.selectedModuleConfig.documentDisplayName;
    }

    @computed
    get searchMillisecondTimeout(): number {
        if (this.globalConfig && this.globalConfig.searchMillisecondTimeout) {
            return this.globalConfig.searchMillisecondTimeout;
        }
        return 600;
    }

    @computed
    get autoSearchMinChar(): number {
        if (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.search &&
            this.selectedModuleConfig.search.config &&
            this.selectedModuleConfig.search.config.autoSearchMinChar
        ) {
            return this.selectedModuleConfig.search.config.autoSearchMinChar;
        }
        if (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.autoSearchMinChar
        ) {
            return this.selectedModuleConfig.autoSearchMinChar;
        }
        if (this.globalConfig && this.globalConfig.autoSearchMinChar) {
            return this.globalConfig.autoSearchMinChar;
        }

        return 1;
    }

    @computed
    get cultureSearchTransform(): CultureSearchTransform | undefined {
        if (
            this.selectedModuleConfig &&
            this.selectedModuleConfig.cultureSearchTransform
        ) {
            return this.selectedModuleConfig.cultureSearchTransform;
        }
        if (this.globalConfig && this.globalConfig.cultureSearchTransform) {
            return this.globalConfig.cultureSearchTransform;
        }
        return;
    }

    @computed
    get fundGroupPropertyId(): string {
        return this.selectedModuleConfig.fundGroupPropertyId;
    }

    @computed
    get rowConfig(): RowConfig {
        const rowConfig = this.selectedModuleConfig.rowConfig;
        // Create unique key for link filtering.
        if (rowConfig && rowConfig.header) {
            rowConfig.header.key = rowConfig.header.key || generateGuid();
        }

        return rowConfig || {};
    }

    @computed
    get rowConfigFootnoteScope(): FootnoteScope {
        return this.rowConfig.footnoteScope || 'isolated';
    }

    @computed
    get rowConfigFootnoteOptions() {
        if (this.rowConfigFootnoteScope === 'shared') {
            return this.moduleFootnoteOptions;
        }
        return this.generateFootnoteOptions(this.rowConfig);
    }

    @computed
    get rawRowConfig() {
        return this.rowConfig as RowConfig;
    }

    @computed
    get rowChartsConfig() {
        const { charts } = this.rawRowConfig;

        if (charts) {
            // Backwards compatibility
            if (isArrayLike(charts)) {
                return charts;
            }
            if (charts.config) {
                return charts.config;
            }
        }
        return [];
    }

    @computed
    get ratingImageOptions(): RatingImageOptions {
        return this.selectedModuleConfig.rowConfig.ratingImageOptions;
    }

    private generateFootnoteOptions(dataSource: any): FootnoteOptions {
        const {
            footnoteLinks,
            footnoteScheme,
            footnoteSymbolFallback,
            footnoteCharacters,
        } = dataSource;
        return {
            type: footnoteScheme || 'numeric',
            symbols: footnoteCharacters,
            fallback: footnoteSymbolFallback,
            enableAnchors: footnoteLinks,
        };
    }

    @computed
    get documentsGrid() {
        const docGrid: DocumentsGrid = this.selectedModuleConfig.rowConfig
            ?.documentsGrid;
        if (docGrid) {
            if (docGrid?.actions?.start) {
                this.generateKeyForLinkElement(docGrid.actions.start);
            }
            if (docGrid?.actions?.end) {
                this.generateKeyForLinkElement(docGrid.actions.end);
            }
            return docGrid;
        }
    }

    @computed
    get documentsGridConditionals() {
        const docGridConditionals: Array<DocumentsGridConditional> = this
            .selectedModuleConfig.rowConfig?.documentsGridConditionals;
        if (docGridConditionals?.length > 0) {
            for (const docGrid of docGridConditionals) {
                if (docGrid.config?.actions?.start) {
                    this.generateKeyForLinkElement(
                        docGrid.config.actions.start
                    );
                }
                if (docGrid.config?.actions?.end) {
                    this.generateKeyForLinkElement(docGrid.config.actions.end);
                }
            }
            return docGridConditionals;
        }
    }

    @computed
    get extendedInfo(): ExtendedInfo {
        const extendedInfo =
            this.selectedModuleConfig.extendedInfo ||
            this.selectedFundType.extendedInfo ||
            this.globalConfig.extendedInfo;
        return extendedInfo;
    }

    @computed
    get stickyConfig(): StickyConfig | undefined {
        return this.selectedModuleConfig.stickyOptions;
    }

    /**
     * Gets the extendedInfo options based on the order of Module -> Fund -> Global config.
     */
    @computed
    get extendedInfoOptions(): Map<string, ExtendedInfoOption> {
        if (this.extendedInfo) {
            const optionsMap = new Map<string, ExtendedInfoOption>();
            // merge options to show icons from all configured sections. The map takes unique keys and sets the values to overwrite
            // config with the same key. This allows us to overwrite the global config with a module config
            this.buildMap(
                optionsMap,
                toJS(this.globalConfig.extendedInfo?.options)
            );
            this.buildMap(
                optionsMap,
                toJS(this.selectedFundType.extendedInfo?.options)
            );
            this.buildMap(
                optionsMap,
                toJS(this.selectedModuleConfig.extendedInfo?.options)
            );

            return optionsMap;
        }
    }

    @computed
    get defaultDocumentSelectionState(): DefaultDocumentSelectionState {
        return (
            this.selectedModuleConfig.downloadControlDefaults?.selectionState ||
            'selected'
        );
    }

    @computed
    get zeroDocumentCountOverride(): string | undefined {
        return this.selectedModuleConfig.downloadControlDefaults
            ?.zeroCountOverride;
    }

    @computed
    get hideZeroDocumentCount(): boolean {
        return (
            this.selectedModuleConfig.downloadControlDefaults?.hideZeroCount ||
            false
        );
    }

    private buildMap(map: Map<string, object>, workingObject: object) {
        if (workingObject) {
            for (const key of Object.keys(workingObject)) {
                map.set(key, workingObject[key]);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/ConfigStore.ts